import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { 
  Crown,
  Calendar,
  CreditCard,
  Users,
  Brain,
  Zap,
  CheckCircle2,
  AlertTriangle,
  Gift,
  ArrowUpCircle,
  Plus,
  RefreshCw,
  Star,
  TrendingUp,
  Clock,
  Building2
} from 'lucide-react';
import { apiRequest } from '@/utils/api-fetch';

// Simple Card components for UI consistency
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children, className = '' }) => (
  <div className={`p-6 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children, className = '' }) => (
  <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>
    {children}
  </h3>
);

const CardDescription = ({ children, className = '' }) => (
  <p className={`text-sm text-gray-600 mt-1 ${className}`}>
    {children}
  </p>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`p-6 ${className}`}>
    {children}
  </div>
);

const Progress = ({ value, className = '' }) => (
  <div className={`w-full bg-gray-200 rounded-full ${className}`}>
    <div 
      className="bg-blue-600 h-full rounded-full transition-all duration-300"
      style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
    />
  </div>
);

const SubscriptionPage = () => {
  const [subscription, setSubscription] = useState(null);
  const [credits, setCredits] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [couponStatus, setCouponStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfileData();
    fetchAvailablePlans();
    fetchCouponStatus();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const result = await apiRequest('users/profile');

      if (result.success) {
        setProfile(result.data.profile);
        setSubscription(result.data.subscription);
        setCredits(result.data.credits);
        setStats(result.data.interviewStats);
      }
    } catch (error) {
      console.error('Error fetching profile data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailablePlans = async () => {
    try {
      const result = await apiRequest('plans');
      if (result.success) {
        setAvailablePlans(result.data);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const fetchCouponStatus = async () => {
    try {
      const result = await apiRequest('coupons/user-status');
      if (result.success) {
        setCouponStatus(result.data);
      }
    } catch (error) {
      console.error('Error fetching coupon status:', error);
    }
  };


  const handleUpgrade = (planId) => {
    navigate(`/pricing?selectedPlan=${planId}`);
  };

  const handleBuyCredits = () => {
    navigate('/buy-credits');
  };

  const getRemainingDays = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const getUsagePercentage = (used, total) => {
    if (!total || total === 0) return 0;
    return Math.min(100, Math.round((used / total) * 100));
  };

  const getPlanIcon = (planName) => {
    switch (planName?.toLowerCase()) {
      case 'free':
        return <Star className="w-5 h-5 text-green-500" />;
      case 'basic':
        return <Zap className="w-5 h-5 text-blue-500" />;
      case 'pro':
        return <Crown className="w-5 h-5 text-purple-500" />;
      case 'enterprise':
        return <Building2 className="w-5 h-5 text-orange-500" />;
      default:
        return <Star className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'expired':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'cancelled':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Crown className="w-12 h-12 animate-pulse text-purple-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading subscription details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Subscription & Credits</h1>
          <p className="text-gray-600">
            Manage your subscription plan and interview credits
          </p>
        </div>

        {/* Current Subscription */}
        {subscription && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {getPlanIcon(subscription.planName)}
                Current Plan: {subscription.planName}
                <span className={`ml-2 px-2 py-1 rounded-full text-sm font-medium ${getStatusColor(subscription.status)}`}>
                  {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                </span>
              </CardTitle>
              <CardDescription>
                {subscription.planDescription}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                {/* Plan Details */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Plan Details</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Monthly Cost:</span>
                      <span className="font-medium">
                        ${subscription.price?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Billing Cycle:</span>
                      <span className="font-medium">Monthly</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Auto Renew:</span>
                      <span className="font-medium">
                        {subscription.autoRenew ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Max Interviews:</span>
                      <span className="font-medium">
                        {subscription.maxInterviewsPerMonth || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Dates */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Subscription Dates</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="text-gray-600">Started:</div>
                        <div className="font-medium">
                          {new Date(subscription.startDate).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="text-gray-600">
                          {subscription.status === 'active' ? 'Renews:' : 'Expired:'}
                        </div>
                        <div className="font-medium">
                          {new Date(subscription.endDate).toLocaleDateString()}
                          <span className="ml-2 text-gray-500">
                            ({getRemainingDays(subscription.endDate)} days)
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Actions</h4>
                  <div className="space-y-3">
                    {subscription.planName !== 'Enterprise' && (
                      <button
                        onClick={() => handleUpgrade(subscription.id)}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md flex items-center justify-center"
                      >
                        <ArrowUpCircle className="w-4 h-4 mr-2" />
                        Upgrade Plan
                      </button>
                    )}
                    <button
                      onClick={() => navigate('/pricing')}
                      className="w-full border border-gray-300 text-gray-700 px-4 py-2 rounded-md flex items-center justify-center hover:bg-gray-50"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Change Plan
                    </button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Credits & Usage */}
          <div className="lg:col-span-2 space-y-8">
            {/* Credits */}
            {credits && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gift className="w-5 h-5 text-green-500" />
                    Interview Credits
                  </CardTitle>
                  <CardDescription>
                    Your available interview credits and usage
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Credits Overview */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">
                          {credits.remaining || 0}
                        </div>
                        <div className="text-sm text-green-700">Remaining</div>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">
                          {credits.used || 0}
                        </div>
                        <div className="text-sm text-blue-700">Used</div>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-600">
                          {credits.total || 0}
                        </div>
                        <div className="text-sm text-gray-700">Total</div>
                      </div>
                    </div>

                    {/* Usage Progress */}
                    {credits.total > 0 && (
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-gray-600">Credits Used</span>
                          <span className="font-medium">
                            {credits.used} of {credits.total}
                          </span>
                        </div>
                        <Progress 
                          value={getUsagePercentage(credits.used, credits.total)}
                          className="h-3"
                        />
                      </div>
                    )}

                    {/* Buy Credits Button */}
                    <button
                      onClick={handleBuyCredits}
                      className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center justify-center"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Buy More Credits
                    </button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Usage Stats */}
            {stats && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                    Usage Statistics
                  </CardTitle>
                  <CardDescription>
                    Your activity over the past 30 days
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {stats.totalInterviews || 0}
                      </div>
                      <div className="text-sm text-gray-600">Total Interviews</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {stats.completedInterviews || 0}
                      </div>
                      <div className="text-sm text-gray-600">Completed</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">
                        {stats.inProgressInterviews || 0}
                      </div>
                      <div className="text-sm text-gray-600">In Progress</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-600">
                        {stats.interviewsThisMonth || 0}
                      </div>
                      <div className="text-sm text-gray-600">This Month</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Coupon Status */}
            {couponStatus && couponStatus.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gift className="w-5 h-5 text-yellow-500" />
                    Active Coupons
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {couponStatus.map((coupon, index) => (
                      <div key={index} className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-green-600">
                            {coupon.code}
                          </span>
                          <span variant="secondary" className="text-xs">
                            {coupon.remaining_uses} left
                          </span>
                        </div>
                        <p className="text-sm text-gray-600">
                          {coupon.description}
                        </p>
                        <div className="text-xs text-gray-500 mt-2">
                          Expires: {new Date(coupon.valid_to).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/pricing')}
                    className="w-full border border-gray-300 text-gray-700 px-4 py-2 rounded-md flex items-center justify-start gap-2 hover:bg-gray-50"
                  >
                    <Crown className="w-4 h-4" />
                    View All Plans
                  </button>
                  <button
                    onClick={() => navigate('/transactions')}
                    className="w-full border border-gray-300 text-gray-700 px-4 py-2 rounded-md flex items-center justify-start gap-2 hover:bg-gray-50"
                  >
                    <CreditCard className="w-4 h-4" />
                    Transaction History
                  </button>
                  <button
                    onClick={() => navigate('/interviews/new')}
                    className="w-full border border-gray-300 text-gray-700 px-4 py-2 rounded-md flex items-center justify-start gap-2 hover:bg-gray-50"
                  >
                    <Users className="w-4 h-4" />
                    Start Interview
                  </button>
                  <button
                    onClick={() => navigate('/support')}
                    className="w-full border border-gray-300 text-gray-700 px-4 py-2 rounded-md flex items-center justify-start gap-2 hover:bg-gray-50"
                  >
                    <Brain className="w-4 h-4" />
                    Get Support
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Plan Recommendations */}
            {subscription && availablePlans.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Recommended Upgrades</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {availablePlans
                      .filter(plan => 
                        plan.plan_id > subscription.plan_id && 
                        plan.status === 'active'
                      )
                      .slice(0, 2)
                      .map((plan) => (
                        <div key={plan.plan_id} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {getPlanIcon(plan.name)}
                              <span className="font-medium">{plan.name}</span>
                            </div>
                            <span className="text-sm bg-gray-100 text-gray-700 px-2 py-1 rounded">
                              ${plan.price}/month
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">
                            {plan.description}
                          </p>
                          <button
                            onClick={() => handleUpgrade(plan.plan_id)}
                            className="w-full bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded text-sm"
                          >
                            Upgrade
                          </button>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;